'use strict';

define('vb/helpers/bootstrapRest',['vb/private/helpers/rest', 'vb/private/configLoader'], (RestHelper, ConfigLoader) => {
  /**
   * @callback GetEndpointCallback
   * @param {any} endpointRef
   * @param {{ extensionId: string }} _serviceContext
   * @param {any} serverVars
   * @returns {Promise<*>}
   */

  /**
   * @typedef {{ extensionId: string }} BootstrapServiceContext
   * @typedef {{ getEndpoint: GetEndpointCallback }} EndpointProvider
   */

  /**
   * serves the same purpose as Rest helper, but is intended for use in contexts before Application is created.
   * We would document specifically where this should be used: security providers
   *
   * The differences are:
   *  - this will only work for services that do not use any $dollar-vars (except $initParams) in expressions
   *    (which really shouldn't be happening, because it wouldn't make sense, but we don't know)
   * - this only works for the "services" in application; it would not look in other flows for "services"
   *
   * The existing Rest helper is not re-used for this, to be explicit about the different contexts,
   * since it is possible (for better or worse) to use $application.variables after Application has been created.
   *
   * Use of anything other than $initParams in path expressions should be 'deprecated',
   * but it is unclear if that will be possible.
   * @category Helper
   */
  class BootstrapRest extends RestHelper {
    /**
     * @param {string} id
     */
    constructor(id) {
      /** @type {BootstrapServiceContext} */
      const serviceContext = { extensionId: 'base' };
      super(id, 'vb/private/services/servicesManager', serviceContext);
    }

    /**
     * factory method, same as Rest.get
     * @param {string} id
     * @returns {BootstrapRest}
     */
    static get(id) {
      return new BootstrapRest(id);
    }

    /**
     * Overrides the inherited endpoint-provider resolution so services are read from the app-flow
     * configuration before Application has been created. This means only $initParams will be available
     * for path expressions.
     *
     * @see AbstractRestHelper#_getEndpointProvider
     * @returns {Promise<EndpointProvider>}
     * @package
     */
    // eslint-disable-next-line class-methods-use-this
    _getEndpointProvider() {
      return Promise.resolve({
        getEndpoint: (endpointRef, _serviceContext, serverVars) => ConfigLoader.getServices()
          .then((services) => services.getEndpoint(endpointRef, serverVars)),
      });
    }
  }

  return BootstrapRest;
});

